/**
 * Kenney Casino Audio (CC0) — poker / blackjack / slots SFX.
 *
 * Local:  /audio/kenney-casino/ogg/*.ogg  (Worker static — primary)
 * CDN:    https://assets.grudge-studio.com/audio/kenney/casino/*.ogg
 *
 * Source: Documents/kenney_casino-audio.zip · License CC0 Kenney.nl
 * @see docs/CASINO_AUDIO_SSOT.md
 */

const CDN_BASE = "https://assets.grudge-studio.com/audio/kenney/casino";
const LOCAL_BASE = "/audio/kenney-casino/ogg";

/** Prefer same-origin (CF Worker assets); fall back to fleet CDN */
function urlFor(file: string): string {
  const name = file.endsWith(".ogg") ? file : `${file}.ogg`;
  // Same-origin first for poker.grudge-studio.com deploy
  return `${LOCAL_BASE}/${name}`;
}

export function casinoCdnUrl(file: string): string {
  const name = file.endsWith(".ogg") ? file : `${file}.ogg`;
  return `${CDN_BASE}/${name}`;
}

/** Semantic events → Kenney pack files (variants rotate) */
export const CASINO_SFX = {
  // Cards
  deal: ["card-slide-1", "card-slide-2", "card-slide-3", "card-slide-4"],
  dealFan: ["card-fan-1", "card-fan-2"],
  place: ["card-place-1", "card-place-2", "card-place-3", "card-place-4"],
  fold: ["card-shove-1", "card-shove-2", "card-shove-3", "card-shove-4"],
  shuffle: ["card-shuffle"],
  packOpen: ["cards-pack-open-1", "cards-pack-open-2"],
  packTake: ["cards-pack-take-out-1", "cards-pack-take-out-2"],
  // Chips / bets
  chipLay: ["chip-lay-1", "chip-lay-2", "chip-lay-3"],
  chipStack: [
    "chips-stack-1",
    "chips-stack-2",
    "chips-stack-3",
    "chips-stack-4",
  ],
  chipCollide: [
    "chips-collide-1",
    "chips-collide-2",
    "chips-collide-3",
    "chips-collide-4",
  ],
  chipHandle: [
    "chips-handle-1",
    "chips-handle-2",
    "chips-handle-3",
    "chips-handle-4",
  ],
  // Dice / slots spin flair
  diceShake: ["dice-shake-1", "dice-shake-2", "dice-shake-3"],
  diceThrow: ["dice-throw-1", "dice-throw-2", "dice-throw-3", "die-throw-1"],
  diceGrab: ["dice-grab-1", "dice-grab-2"],
} as const;

export type CasinoSfxEvent = keyof typeof CASINO_SFX;

const VARIANT_I: Partial<Record<CasinoSfxEvent, number>> = {};

function nextVariant(event: CasinoSfxEvent): string {
  const list = CASINO_SFX[event] as readonly string[];
  const i = (VARIANT_I[event] ?? 0) % list.length;
  VARIANT_I[event] = i + 1;
  return list[i];
}

const pool = new Map<string, HTMLAudioElement[]>();
const MAX_POOL = 4;

function getAudio(fileStem: string): HTMLAudioElement {
  const key = fileStem;
  let list = pool.get(key);
  if (!list) {
    list = [];
    pool.set(key, list);
  }
  const idle = list.find((a) => a.paused || a.ended);
  if (idle) {
    idle.currentTime = 0;
    return idle;
  }
  if (list.length < MAX_POOL) {
    const a = new Audio(urlFor(fileStem));
    a.preload = "auto";
    list.push(a);
    return a;
  }
  // Reuse oldest
  const a = list[0];
  a.pause();
  a.currentTime = 0;
  return a;
}

let settingsCache: { enabled: boolean; volume: number; at: number } | null =
  null;

function readSettings(): { enabled: boolean; volume: number } {
  const now = Date.now();
  if (settingsCache && now - settingsCache.at < 2000) {
    return { enabled: settingsCache.enabled, volume: settingsCache.volume };
  }
  try {
    const raw = localStorage.getItem("poker_settings");
    if (raw) {
      const s = JSON.parse(raw);
      const enabled = s.soundEnabled !== false;
      const volume = Math.min(
        1,
        Math.max(0, (Number(s.soundVolume) || 75) / 100),
      );
      settingsCache = { enabled, volume, at: now };
      return { enabled, volume };
    }
  } catch {
    /* ignore */
  }
  settingsCache = { enabled: true, volume: 0.75, at: now };
  return { enabled: true, volume: 0.75 };
}

let unlocked = false;

/** Call once on first user gesture so mobile browsers allow audio */
export function unlockCasinoAudio(): void {
  if (unlocked || typeof window === "undefined") return;
  unlocked = true;
  try {
    const a = new Audio(urlFor("chip-lay-1"));
    a.volume = 0.01;
    void a.play().then(() => {
      a.pause();
    }).catch(() => {
      unlocked = false;
    });
  } catch {
    unlocked = false;
  }
}

export function playCasinoSfx(
  event: CasinoSfxEvent,
  opts?: { volume?: number; rate?: number },
): void {
  if (typeof window === "undefined") return;
  const { enabled, volume: volSet } = readSettings();
  if (!enabled) return;

  const stem = nextVariant(event);
  try {
    const a = getAudio(stem);
    a.volume = Math.min(1, Math.max(0, opts?.volume ?? volSet));
    if (opts?.rate) a.playbackRate = opts.rate;
    void a.play().catch(() => {
      // Try CDN fallback once
      a.src = casinoCdnUrl(stem);
      void a.play().catch(() => {});
    });
  } catch {
    /* ignore */
  }
}

/** Staggered deal (hole cards / street) */
export function playDealSequence(count: number, gapMs = 90): void {
  for (let i = 0; i < count; i++) {
    window.setTimeout(() => playCasinoSfx("deal"), i * gapMs);
  }
}

/** Semantic helpers for games */
export const casino = {
  unlock: unlockCasinoAudio,
  play: playCasinoSfx,
  deal: (n = 1) => (n <= 1 ? playCasinoSfx("deal") : playDealSequence(n)),
  dealBoard: () => playCasinoSfx("place"),
  fold: () => playCasinoSfx("fold"),
  shuffle: () => playCasinoSfx("shuffle"),
  bet: () => playCasinoSfx("chipLay"),
  call: () => playCasinoSfx("chipStack"),
  raise: () => playCasinoSfx("chipCollide"),
  win: () => playCasinoSfx("chipHandle"),
  check: () => playCasinoSfx("chipLay", { volume: 0.35 }),
  /** Blackjack 21 / natural */
  blackjack: () => {
    playCasinoSfx("chipCollide");
    window.setTimeout(() => playCasinoSfx("chipHandle"), 120);
  },
  hit: () => playCasinoSfx("deal"),
  stand: () => playCasinoSfx("place"),
  bust: () => playCasinoSfx("fold"),
  spinStart: () => playCasinoSfx("diceShake"),
  spinStop: () => playCasinoSfx("diceThrow"),
  pack: () => playCasinoSfx("packOpen"),
};

/** Full pack inventory for docs / debug */
export const KENNEY_CASINO_FILES = [
  "card-fan-1",
  "card-fan-2",
  "card-place-1",
  "card-place-2",
  "card-place-3",
  "card-place-4",
  "card-shove-1",
  "card-shove-2",
  "card-shove-3",
  "card-shove-4",
  "card-shuffle",
  "card-slide-1",
  "card-slide-2",
  "card-slide-3",
  "card-slide-4",
  "card-slide-5",
  "card-slide-6",
  "card-slide-7",
  "card-slide-8",
  "cards-pack-open-1",
  "cards-pack-open-2",
  "cards-pack-take-out-1",
  "cards-pack-take-out-2",
  "chip-lay-1",
  "chip-lay-2",
  "chip-lay-3",
  "chips-collide-1",
  "chips-collide-2",
  "chips-collide-3",
  "chips-collide-4",
  "chips-handle-1",
  "chips-handle-2",
  "chips-handle-3",
  "chips-handle-4",
  "chips-handle-5",
  "chips-handle-6",
  "chips-stack-1",
  "chips-stack-2",
  "chips-stack-3",
  "chips-stack-4",
  "chips-stack-5",
  "chips-stack-6",
  "dice-grab-1",
  "dice-grab-2",
  "dice-shake-1",
  "dice-shake-2",
  "dice-shake-3",
  "dice-throw-1",
  "dice-throw-2",
  "dice-throw-3",
  "die-throw-1",
  "die-throw-2",
  "die-throw-3",
  "die-throw-4",
] as const;
